/**
 * Chat send message handler — sends user message and processes AI response.
 */

import { runInAction } from 'mobx';
import type { ChatEvent } from '@skjv/common';
import { getDeviceId } from '../persistence/device-id';
import { fetchChatResponseStreaming, QueueFullError } from '@api/chat';
import type { ChatMessage } from './types';
import { ensureConversation } from './conversation-ops';
import { handleProgressEvent } from './progress-handler';
import { waitForTheaterDone } from './theater-events';
import type { ChatState } from './chat-state';
import type { ReceiptItem } from './types';

/** Send a message and get an AI response */
export async function sendMessage(state: ChatState, content: string): Promise<void> {
  await ensureConversation(state);

  const userMsg: ChatMessage = {
    id: state.nextId(),
    role: 'user',
    content,
    timestamp: new Date().toISOString(),
  };
  state.messages.push(userMsg);

  const loadingId = state.nextId();
  state.messages.push({
    id: loadingId,
    role: 'assistant',
    content: '',
    timestamp: new Date().toISOString(),
    isLoading: true,
  });
  state.isLoading = true;

  try {
    const response = await fetchChatResponseStreaming(content, {
      conversationId: state.activeConversationId ?? undefined,
      deviceId: getDeviceId() || undefined,
      onProgress: (event: ChatEvent) => handleProgressEvent(
        state.messages,
        (v) => { state.isLoading = v; },
        () => state.persistLocal(),
        loadingId,
        event,
      ),
    });

    // Let the edit theater finish before the typeset (no-op without a show).
    await waitForTheaterDone(state.messages, loadingId);

    runInAction(() => {
      const idx = state.messages.findIndex((m) => m.id === loadingId);
      if (idx !== -1) {
        const loadingMsg = state.messages[idx];
        if (!loadingMsg.scriptureResponse && !loadingMsg.locatorResults && !loadingMsg.searchResults) {
          const responseContent = response.mlResponse ?? response.response ?? '';
          state.messages[idx] = {
            id: loadingId,
            role: 'assistant',
            content: responseContent,
            timestamp: new Date().toISOString(),
            editReceipt:
              (response as { editReceipt?: ReceiptItem[] }).editReceipt ?? loadingMsg.editReceipt,
            stats: response.stats,
            intent: response.intent?.intent,
            metadata: response.metadata,
            commentary: response.commentary,
            rawModelBody: response.rawModelBody,
            contentChunks: loadingMsg.contentChunks,
            itIsWritten: response.itIsWritten,
            reference: response.reference,
            strongsAnalysis: response.strongsAnalysis,
            textualVariant: response.textualVariant,
            crossReferences: response.crossReferences,
            pericope: response.pericope,
          };
        } else {
          state.messages[idx] = { ...loadingMsg, originalQuery: content, queueStatus: undefined };
        }
      }
      state.isLoading = false;
      state.persistLocal();
      state.updateConversationInList(content);
    });
  } catch (err) {
    runInAction(() => {
      const msg = err instanceof Error ? err.message : '';
      // Friendly, non-error outcomes: cold model and full queue.
      const isWarmup = msg.includes('warming up');
      const isQueueFull = err instanceof QueueFullError;
      const idx = state.messages.findIndex((m) => m.id === loadingId);
      if (idx !== -1) {
        state.messages[idx] = {
          id: loadingId,
          role: 'assistant',
          content: isWarmup || isQueueFull
            ? msg
            : 'Unable to reach the server. Please try again later.',
          timestamp: new Date().toISOString(),
          isError: !isWarmup && !isQueueFull,
        };
      }
      state.isLoading = false;
      state.persistLocal();
    });
  }
}
